import { Box, Button, Modal, Typography, TextField, useTheme } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { useCreateVoice } from 'apis/business/agent/voice/hook';
import MicIcon from 'assets/icons/MicIcon';

export default function CloneVoiceModal() {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isFinishButtonDisabled, setIsFinishButtonDisabled] = useState(true);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [voiceName, setVoiceName] = useState('');

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const { mutate: createVoice } = useCreateVoice();

  const { startRecording, stopRecording } = useReactMediaRecorder({
    audio: true,
    onStop: (blobUrl, blob) => {
      setAudioBlob(blob);
    }
  });

  useEffect(() => {
    if (isOpen) {
      startVisualization();
    }

    return () => {
      stopVisualization();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;

      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      visualize();
    } catch (error) {
      console.error('Error accessing microphone for visualization:', error);
    }
  };

  const stopVisualization = () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    }

    analyserRef.current = null;
    dataArrayRef.current = null;
  };

  const visualize = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context || !analyserRef.current || !dataArrayRef.current) {
      return;
    }

    const draw = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      context.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / dataArrayRef.current.length) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < dataArrayRef.current.length; i++) {
        barHeight = dataArrayRef.current[i];
        context.fillStyle = theme.palette.primary.main; // Color of bars
        context.fillRect(x, canvas.height - barHeight / 10, barWidth, barHeight / 2);
        x += barWidth + 1;
      }

      requestAnimationFrame(draw);
    };

    draw();
  };

  const handleOpen = () => {
    setIsOpen(true);
    startRecording();
    startTimer();
  };

  const handleClose = () => {
    stopRecording();
    setIsOpen(false);
    stopTimer();
    stopVisualization();
  };

  const resetRecording = () => {
    stopRecording();
    stopTimer();
    stopVisualization();
    setTimeElapsed(0);
    startRecording();
    startTimer();
    startVisualization();
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeElapsed((prev) => {
        if (prev + 1 === 30) {
          setIsFinishButtonDisabled(false);
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current!);
  };

  const handleFinish = () => {
    stopRecording();
    stopTimer();
    stopVisualization();
    setIsOpen(false);
    setIsSecondModalOpen(true);
  };

  const handleFileUpload = () => {
    if (!audioBlob) return;

    const formData = new FormData();
    formData.append('voice[audio]', audioBlob, 'recording.wav');
    formData.append('voice[name]', voiceName);

    createVoice(formData);
    setIsSecondModalOpen(false);
  };

  return (
    <>
      <Button
        fullWidth
        onClick={handleOpen}
        sx={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
          paddingY: '10px'
        }}
      >
        <MicIcon />
        Clone your voice
      </Button>
      <Modal
        open={isOpen}
        onClose={() => {
          handleClose();
          resetRecording();
        }}
      >
        <Box sx={style}>
          <Typography variant='h6' align='center' gutterBottom fontSize={12}>
            CLONING YOUR VOICE
          </Typography>
          <Typography variant='h6' fontWeight={700} align='center' gutterBottom>
            Talk about your favorite city.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <canvas ref={canvasRef} height='50' width='300' style={{ marginBottom: '16px' }} />
          </Box>

          <Typography variant='body1' align='center' gutterBottom>
            <span style={{ color: 'red' }}>{`0:${timeElapsed < 10 ? '0' : ''}${timeElapsed} / 0:30`}</span>
          </Typography>

          <Box display='flex' gap={2}>
            <Button variant='outlined' fullWidth onClick={resetRecording}>
              Restart
            </Button>
            <Button variant='contained' fullWidth onClick={handleFinish} disabled={isFinishButtonDisabled}>
              Finish
            </Button>
          </Box>
          <Typography fontSize={12} align='center' my={1}>
            Or read along
          </Typography>
          <Typography className='reading-paragraph' variant='body2' color='textSecondary'>
            There's something about traveling that opens your eyes to the beauty of the world. Take my trip to the
            Amazon Rainforest, for example. Being there, amidst such vibrant nature, it's an indescribable feeling. It
            makes you wonder—how much of the world do we take for granted? Every trip, every new landscape, changes you
            a little, doesn't it? It makes you appreciate the planet more. And it's not just about seeing new places;
            it's about the perspectives you gain. It makes you want to explore even further.
          </Typography>
        </Box>
      </Modal>

      <Modal open={isSecondModalOpen}>
        <Box sx={style}>
          <Typography variant='h6' align='center' gutterBottom>
            Name your voice
          </Typography>
          <TextField label='Voice Name' fullWidth value={voiceName} onChange={(e) => setVoiceName(e.target.value)} />
          <Button variant='contained' fullWidth sx={{ mt: 2 }} onClick={handleFileUpload} disabled={!voiceName}>
            Submit
          </Button>
        </Box>
      </Modal>
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 4,
  borderRadius: '4px'
};
